<script setup lang="ts">
import type { ISatOtpMethodSelectorProps } from "@chatfood/design-system";
import { useToast, SatOtpMethodSelector } from "@chatfood/design-system";
import { ref, computed } from "vue";
import { bemBuilder, phoneBeautifier } from "@chatfood/core-utils";
import { AuthRepoError } from "@chatfood/dbe-client/errors";
import type { IOtpDeliverySelectorProps } from "./props";
import type { IOtpDeliverySelectorEmits } from "./emits";
import { useBusinessStore } from "~/stores/business";
import { useLocale } from "~/composables/use-locale";
import { useBugReporter, useDirectBackend } from "#imports";

const props = defineProps<IOtpDeliverySelectorProps>();
const emits = defineEmits<IOtpDeliverySelectorEmits>();

const { t } = useLocale();
const { report } = useBugReporter();
const { toastError } = useToast();
const { authRepo } = useDirectBackend();

const css = bemBuilder("otp-delivery-selector");

const business = useBusinessStore();

const displayPhoneNumber = computed<string>(() => {
  if (!props.countryCode) {
    return props.phoneNumber;
  }

  const fullPhoneNumber = props.countryCode + props.phoneNumber;

  return (
    phoneBeautifier(props.phoneNumber, props.countryCode) ?? fullPhoneNumber
  );
});

const isRequestingOtp = ref(false);

const onSendOtp = async (
  channel?: ISatOtpMethodSelectorProps["methods"][0],
) => {
  if (!channel) {
    toastError(
      t("components.otp_delivery_selector.otp_selector_validation_error"),
    );
    return;
  }

  isRequestingOtp.value = true;

  const messagingChannel = channel.key === "sms" ? "sms" : "whatsapp";

  try {
    await authRepo.requestOtp({
      businessId: business.id,
      messagingChannel,
      ...(props.countryCode
        ? {
            key: "phone",
            phoneNumber: {
              countryCode: props.countryCode,
              number: props.phoneNumber,
            },
          }
        : {
            key: "email",
            email: props.email,
          }),
    });

    emits("success");
  } catch (err: unknown) {
    if (err instanceof AuthRepoError && err.code === "tooManyRequests") {
      toastError(
        t("components.otp_delivery_selector.error_too_many_otp_requests", {
          seconds: props.throttleSeconds,
        }),
      );

      return;
    }

    toastError(t("components.otp_delivery_selector.request_otp_failure"));
    report(err);

    emits("failure");
  } finally {
    isRequestingOtp.value = false;
  }
};
</script>

<template>
  <SatOtpMethodSelector
    :class="css('')"
    :is-loading="isRequestingOtp"
    :cta="t('components.otp_delivery_selector.otp_selector_cta')"
    :title="t('components.otp_delivery_selector.otp_selector_title')"
    :headline="t('components.otp_delivery_selector.otp_selector_subtext')"
    :method-title="
      t('components.otp_delivery_selector.otp_selector_methods_title')
    "
    :methods="[
      {
        key: 'sms',
        text: t('components.otp_delivery_selector.channel_otp_sms'),
        subText: displayPhoneNumber,
      },
      {
        key: 'whatsapp',
        text: t('components.otp_delivery_selector.channel_otp_whatsapp'),
        subText: displayPhoneNumber,
      },
    ]"
    @select="onSendOtp"
  />
</template>
